import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useFetchSingleLoan } from '../hooks/loan/useFetchSingleLoan';
import { LoanOfferTabs, LoanStatusId } from '../interfaces';
import Page from '../layout/components/Page';
import { UserTypeId } from '../helpers/constants';
import { LoanInformation } from '../components/Loan/Loans/LoanInformation';
import { LoanOffers } from '../components/Loan/Loans/LoanOffers';
import LoadingIndicator from '../components/Common/LoadingIndicator';
import VerifiedBadge from '../components/Common/VerifiedBadge';
import LoanTypeBadge from '../components/Common/LoanTypeBadge';
import LoanStatusBadge from '../components/Common/LoanStatusBadge';
import LocationBadge from '../components/Common/LocationBadge';
import { useFetchLoanOffers } from '../hooks/loan/useFetchLoanOffers';
import { Repayments } from '../components/Loan/Loans/Repayments';
import { Transactions } from '../components/Loan/Loans/Transactions';
import { RootState } from '../states/reducers';
import { useSelector } from 'react-redux';
import MakeLoanOffer from '../components/Loan/Loans/MakeLoanOffer';
import DisburseLoan from '../components/Loan/Loans/DisburseLoan';
import { useFetchLoanRepayments } from '../hooks/loan/useFetchLoanRepayments';
import { useFetchLoanTransactions } from '../hooks/loan/useFetchLoanTransactions';
import { RepayLoan } from '../components/Loan/Loans/RepayLoan';

const LoanPage = () => {
  const { loan, canDisburseLoan } = useSelector((state: RootState) => state.loan);
  const { user } = useSelector((state: RootState) => state.auth.authUser!);

  // fetch loan detail
  const { tab, fetching: fetchingLoan, makeActive } = useFetchSingleLoan();
  // fetch loan offers
  const { loadingOffers } = useFetchLoanOffers();
  // fetch loan repayments
  const { fetchingRepayments } = useFetchLoanRepayments();
  // fetch loan transactions
  const { fetchingTransactions, transactions } = useFetchLoanTransactions();

  const RenderChildren = (
    <>
      <Link type='submit' className='btn btn-sm btn-light-primary btn-active-primary fw-bolder me-3 my-5' to='/loans'>
        Back to loans
      </Link>

      {user.user_type_id === UserTypeId.INVESTOR && loan?.loan_status_id === LoanStatusId.OPEN && !fetchingLoan && <MakeLoanOffer />}

      {user.user_type_id === UserTypeId.BORROWER && loan?.loan_status_id === LoanStatusId.ONGOING && !fetchingLoan && <RepayLoan />}

      {user.user_type_id === UserTypeId.INVESTOR && loan?.loan_status_id === LoanStatusId.UNDERWRITING && !fetchingLoan && canDisburseLoan && <DisburseLoan />}
    </>
  );

  return (
    <Page title='Manage Loan' icon='bi-briefcase' toolbarChildren={RenderChildren}>
      {fetchingLoan && <LoadingIndicator message='' />}
      {!fetchingLoan && loan && (
        <div className='card'>
          <div className='card-body'>
            <div className='row'>
              <div className='col-lg-8'>
                <h5 className='text-dark fw-bolder text-capitalize'>{loan?.title}</h5>
                <LocationBadge location={`${loan.borrower.address_state.name}, ${loan.borrower.address_country.name}`} />
                <div className='mt-2'>
                  <LoanStatusBadge status={loan.loan_status_id} />
                  <LoanTypeBadge id={loan.loan_type_id} />
                  <VerifiedBadge />
                </div>
              </div>

              <div className='col-lg-4 text-lg-end'>
                {loan.amount && (
                  <div className='mt-3 mb-3'>
                    <span className='text-muted fs-8 fw-bold'>Loan Amount</span>
                    <h5 className='text-primary'>{`${loan?.amount && parseFloat(loan.amount).toLocaleString()}`}</h5>
                  </div>
                )}

                {loan.tenure && (
                  <div className='mt-3 mb-3'>
                    <span className='text-muted fs-8 fw-bold'>Tenure</span>
                    <h5 className='text-primary'>{`${loan?.tenure} Months`}</h5>
                  </div>
                )}

                {loan.interest && (
                  <div className='mt-3 mb-3'>
                    <span className='text-muted fs-8 fw-bold'>Interest</span>
                    <h5 className='text-primary'>{loan?.interest}</h5>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex overflow-auto h-55px mt-10 mb-10 bg-light ps-5'>
              <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-6 fw-bolder flex-nowrap'>
                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6', makeActive('information'))} to={`/loan/${loan?.id}/${LoanOfferTabs.INFORMATION}`}>
                    Information
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6', makeActive('offers'))} to={`/loan/${loan?.id}/${LoanOfferTabs.OFFERS}`}>
                    Offers
                  </Link>
                </li>

                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6', makeActive('repayments'))} to={`/loan/${loan?.id}/${LoanOfferTabs.REPAYMENTS}`}>
                    Repayments
                  </Link>
                </li>

                <li className='nav-item'>
                  <Link className={clsx('nav-link text-active-primary me-6', makeActive('transactions'))} to={`/loan/${loan?.id}/${LoanOfferTabs.TRANSACTIONS}`}>
                    Transactions
                  </Link>
                </li>
              </ul>
            </div>

            {tab === LoanOfferTabs.INFORMATION && <LoanInformation loan={loan!} />}
            {tab === LoanOfferTabs.OFFERS && (loadingOffers ? <LoadingIndicator message='' /> : <LoanOffers />)}
            {tab === LoanOfferTabs.REPAYMENTS && (fetchingRepayments ? <LoadingIndicator message='' /> : <Repayments />)}
            {tab === LoanOfferTabs.TRANSACTIONS && (fetchingTransactions ? <LoadingIndicator message='' /> : <Transactions loanTransactions={transactions!} />)}
          </div>
        </div>
      )}
    </Page>
  );
};

export default LoanPage;
